import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import axios from "axios";
import TinyWeekSelector from "../../components/tiny/TinyWeekSelector";
import { TinyBusy, TinyLoadingOverlay } from "../../components/tiny/TinyLoading";

const API_BASE = "/api/tiny";
const STATUS_BADGE_STYLES = {
  totalCount: {
    color: "#fff",
    background: "linear-gradient(135deg, #17a2b8 0%, #11cdef 100%)",
    borderRadius: "999px",
    padding: "8px 14px",
    fontSize: "12px",
    fontWeight: 700,
    border: "none",
  },
  totalAmount: {
    color: "#fff",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderRadius: "999px",
    padding: "8px 14px",
    fontSize: "12px",
    fontWeight: 700,
    border: "none",
  },
};

const getNextWeek = (weekStr) => {
  const startStr = weekStr.split("-")[0];
  const year = parseInt(startStr.substring(0, 4), 10);
  const month = parseInt(startStr.substring(4, 6), 10) - 1;
  const day = parseInt(startStr.substring(6, 8), 10);

  const startDate = new Date(year, month, day);
  const nextMonday = new Date(startDate);
  nextMonday.setDate(startDate.getDate() + 7);
  const nextSunday = new Date(nextMonday);
  nextSunday.setDate(nextMonday.getDate() + 6);

  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}${m}${d}`;
  };

  return `${formatDate(nextMonday)}-${formatDate(nextSunday)}`;
};

function TinySettlementReviewTab() {
  const [weeks, setWeeks] = useState([]);
  const [types, setTypes] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [week, setWeek] = useState("");
  const [activityType, setActivityType] = useState("");
  const [clubId, setClubId] = useState("");
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [batches, setBatches] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [batchToDelete, setBatchToDelete] = useState(null);
  const [hardDelete, setHardDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // v2.40.0：這個 Tab 原本六支 GET 一個 loading 都沒有 —— 切週 / 切活動類型時
  // 舊的摘要與明細會原地留著、無聲無息地換成新的（或根本沒換，錯了也看不出來）。
  const [baseLoading, setBaseLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  const loadBaseData = useCallback(async () => {
    setBaseLoading(true);
    try {
      const [weeksRes, typesRes, clubsRes] = await Promise.all([
        axios.get(`${API_BASE}/data/weeks`),
        axios.get(`${API_BASE}/settlements/activity-types`),
        axios.get(`${API_BASE}/clubs`),
      ]);

      const dbWeeks = weeksRes.data?.data?.weeks || [];
      if (dbWeeks.length > 0) {
        const nextWeek = getNextWeek(dbWeeks[0]);
        const weeksWithNext = [nextWeek, ...dbWeeks];
        setWeeks(weeksWithNext);
        if (!week) setWeek(nextWeek);
      } else {
        setWeeks([]);
      }
      setTypes(typesRes.data?.data?.types || []);
      setClubs(clubsRes.data?.data?.clubs || []);
    } catch (err) {
      setError(`載入基礎資料失敗: ${err.response?.data?.msg || err.message}`);
    } finally {
      setBaseLoading(false);
    }
  }, [week]);

  const loadData = useCallback(async () => {
    if (!week) return;
    setError("");
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("week", week);
      if (activityType) params.append("activity_type", activityType);
      if (clubId) params.append("club_id", clubId);

      const [summaryRes, itemsRes, batchesRes] = await Promise.all([
        axios.get(`${API_BASE}/settlement-center/summary/${week}`),
        axios.get(`${API_BASE}/settlement-center/items?${params}`),
        axios.get(`${API_BASE}/settlement-center/batches?week=${week}`),
      ]);

      setSummary(summaryRes.data?.data || null);
      setItems(itemsRes.data?.data?.items || []);
      setBatches(batchesRes.data?.data?.batches || []);
    } catch (err) {
      setError(`載入週檢視資料失敗: ${err.response?.data?.msg || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [week, activityType, clubId]);

  const openDeleteModal = (batch) => {
    setBatchToDelete(batch);
    setHardDelete(false);
    setDeleteModalOpen(true);
  };

  const handleDeleteBatch = async () => {
    if (!batchToDelete?.batch_id) return;
    try {
      setDeleting(true);
      setError("");
      setSuccess("");
      const res = await axios.delete(
        `${API_BASE}/settlement-center/batches/${batchToDelete.batch_id}?hard_delete=${hardDelete ? 1 : 0}&deleted_by=web`
      );
      if (res.data.code === 1) {
        const mode = res.data?.data?.mode;
        if (mode === "hard_delete") {
          setSuccess(`已永久刪除批次 ${batchToDelete.batch_id}（含逐項資料）`);
        } else {
          setSuccess(`已撤銷批次 ${batchToDelete.batch_id}（逐項資料已停用）`);
        }
        setDeleteModalOpen(false);
        setBatchToDelete(null);
        loadData();
      } else {
        setError(res.data.msg || "刪除批次失敗");
      }
    } catch (err) {
      setError(`刪除批次失敗: ${err.response?.data?.msg || err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    loadBaseData();
  }, [loadBaseData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getClubName = (id) => {
    const club = clubs.find((c) => c.club_id === id);
    return club ? `${club.club_name} (${id})` : id || "-";
  };
  const getTypeName = (typeId) => {
    const t = types.find((t) => t.id === typeId);
    return t ? t.name : typeId || "-";
  };
  const inputStyle = {
    background: "#1a1f2b",
    color: "#fff",
    border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: "6px",
  };
  const cardTitleStyle = { color: "#fff", marginBottom: "10px", fontWeight: 600 };
  const tableHeaderStyle = {
    background: "linear-gradient(135deg, #2d3748 0%, #1f2937 100%)",
    color: "#fff",
    borderBottom: "1px solid rgba(255,255,255,0.12)",
  };
  const tableStyle = {
    color: "rgba(255,255,255,0.88)",
    marginBottom: 0,
    background: "transparent",
  };

  return (
    <Card style={{ background: "#1e2530", border: "1px solid rgba(255,255,255,0.1)" }}>
      <CardBody style={{ padding: "20px" }}>
        {error && (
          <Alert color="danger" toggle={() => setError("")}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert color="success" toggle={() => setSuccess("")}>
            {success}
          </Alert>
        )}

        {baseLoading && (
          <div className="mb-2">
            <TinyBusy text="載入週期、活動類型與俱樂部清單" />
          </div>
        )}
        <Row className="mb-3">
          <Col md={4}>
            <TinyWeekSelector
              label="週期"
              options={weeks}
              value={week}
              onChange={setWeek}
              includeEmptyOption
              markFirstAsNextWeek
              inputStyle={inputStyle}
            />
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label style={{ color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>活動類型</Label>
              <Input type="select" value={activityType} onChange={(e) => setActivityType(e.target.value)} style={inputStyle}>
                <option value="">全部</option>
                {types.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label style={{ color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>俱樂部</Label>
              <Input type="select" value={clubId} onChange={(e) => setClubId(e.target.value)} style={inputStyle}>
                <option value="">全部</option>
                {clubs.map((c) => (
                  <option key={c.club_id} value={c.club_id}>
                    {c.club_name} ({c.club_id})
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
        </Row>

        {summary && (
          <Row className="mb-3">
            <Col>
              <Badge className="mr-2" style={STATUS_BADGE_STYLES.totalCount}>
                匯入筆數 {summary.total_count || 0}
              </Badge>
              <Badge style={STATUS_BADGE_STYLES.totalAmount}>匯入總金額 {summary.total_amount || 0}</Badge>
            </Col>
          </Row>
        )}

        <h6 style={cardTitleStyle}>
          本週明細
          {loading && <TinyBusy text="更新中" style={{ marginLeft: 10 }} />}
        </h6>
        <TinyLoadingOverlay active={loading} text="載入本週明細">
        <div
          style={{
            maxHeight: 320,
            overflow: "auto",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "10px",
            background: "rgba(13,17,23,0.35)",
          }}
        >
          <Table hover size="sm" style={tableStyle}>
            <thead style={tableHeaderStyle}>
              <tr>
                <th>週期</th>
                <th>活動</th>
                <th>俱樂部</th>
                <th>超代</th>
                <th>代理</th>
                <th>玩家</th>
                <th>金額</th>
                <th>來源</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.week}</td>
                  <td>{item.activity_name || getTypeName(item.activity_type)}</td>
                  <td>{getClubName(item.club_id)}</td>
                  <td>{item.super_agent_id || "-"}</td>
                  <td>{item.agent_id || "-"}</td>
                  <td>{item.player_id || "-"}</td>
                  <td>{item.amount}</td>
                  <td>{item.source || "-"}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center" style={{ color: "rgba(255,255,255,0.55)" }}>
                    查無資料
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
        </TinyLoadingOverlay>

        <h6 className="mt-4" style={cardTitleStyle}>匯入批次</h6>
        <div
          style={{
            maxHeight: 260,
            overflow: "auto",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "10px",
            background: "rgba(13,17,23,0.35)",
          }}
        >
          <Table hover size="sm" style={tableStyle}>
            <thead style={tableHeaderStyle}>
              <tr>
                <th>批次</th>
                <th>活動</th>
                <th>有效</th>
                <th>警告</th>
                <th>拒絕</th>
                <th>匯入總金額</th>
                <th>建立時間</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.batch_id}>
                  <td>{b.batch_id}</td>
                  <td>{b.activity_name || b.activity_type}</td>
                  <td>{b.accepted_rows}</td>
                  <td>{b.warning_rows}</td>
                  <td>{b.rejected_rows}</td>
                  <td>{b.accepted_amount}</td>
                  <td>{b.created_at ? new Date(b.created_at).toLocaleString("zh-TW") : "-"}</td>
                  <td>
                    <Button
                      size="sm"
                      style={{
                        background: "linear-gradient(135deg, #f5365c 0%, #f56036 100%)",
                        border: "none",
                        color: "#fff",
                        fontWeight: 600,
                      }}
                      onClick={() => openDeleteModal(b)}
                    >
                      刪除批次
                    </Button>
                  </td>
                </tr>
              ))}
              {batches.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center" style={{ color: "rgba(255,255,255,0.55)" }}>
                    查無資料
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        <Modal isOpen={deleteModalOpen} toggle={() => setDeleteModalOpen(false)}>
          <ModalHeader toggle={() => setDeleteModalOpen(false)}>確認刪除匯入批次</ModalHeader>
          <ModalBody>
            <div style={{ marginBottom: "10px" }}>
              批次：<strong>{batchToDelete?.batch_id || "-"}</strong>
            </div>
            <div style={{ color: "#6c757d", fontSize: "13px", marginBottom: "10px" }}>
              預設只會「撤銷」此批次：逐項資料改為停用，不會再進入週檢視/報表。
            </div>
            <Label check style={{ userSelect: "none" }}>
              <Input type="checkbox" checked={hardDelete} onChange={(e) => setHardDelete(e.target.checked)} />{" "}
              永久刪除（連同批次紀錄與逐項資料一起刪除）
            </Label>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setDeleteModalOpen(false)}>
              取消
            </Button>
            <Button color="danger" onClick={handleDeleteBatch} disabled={deleting}>
              {deleting ? "處理中..." : "確認刪除"}
            </Button>
          </ModalFooter>
        </Modal>
      </CardBody>
    </Card>
  );
}

export default TinySettlementReviewTab;

